import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Calendar,
  Building2,
  ArrowLeft,
  RefreshCw,
  Loader2,
  Search,
  Filter,
  Download,
  FileText,
  AlertCircle,
  TrendingUp,
  Bell,
  ChevronRight,
  ExternalLink,
  Flame,
  Zap,
  Home,
  DollarSign,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compliance type icons and colors
const COMPLIANCE_CONFIG = {
  insurance: { icon: Shield, color: 'indigo', label: 'Insurance Certificate', priority: 1 },
  fire_safety: { icon: Flame, color: 'red', label: 'Fire Safety Certificate', priority: 1 },
  electrical: { icon: Zap, color: 'amber', label: 'Electrical Compliance', priority: 2 },
  building_inspection: { icon: Building2, color: 'blue', label: 'Building Inspection', priority: 3 },
  levy_clearance: { icon: DollarSign, color: 'emerald', label: 'Levy Clearance', priority: 3 },
  occupancy: { icon: Home, color: 'purple', label: 'Occupancy Certificate', priority: 2 },
};

const STATUS_COLORS = {
  expired: 'bg-red-100 text-red-700 border-red-200',
  expiring_soon: 'bg-amber-100 text-amber-700 border-amber-200',
  attention: 'bg-orange-100 text-orange-700 border-orange-200',
  missing: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STATUS_PRIORITY = {
  expired: 1,
  expiring_soon: 2,
  attention: 3,
  missing: 4,
};

export default function ComplianceAlerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, expired: 0, expiring_soon: 0, missing: 0 });
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [daysFilter, setDaysFilter] = useState('60');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchAlerts();
  }, [daysFilter]);

  useEffect(() => {
    filterAlerts();
  }, [alerts, searchQuery, statusFilter, typeFilter, activeTab]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/building-records/alerts/all?days=${daysFilter}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
        setSummary(data.summary || { total: 0, expired: 0, expiring_soon: 0, missing: 0 });
      } else {
        toast.error('Failed to load compliance alerts');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const filterAlerts = () => {
    let filtered = [...alerts];
    
    // Tab filter
    if (activeTab === 'expired') {
      filtered = filtered.filter(a => a.status_info?.status === 'expired');
    } else if (activeTab === 'expiring') {
      filtered = filtered.filter(a => ['expiring_soon', 'attention'].includes(a.status_info?.status));
    } else if (activeTab === 'missing') {
      filtered = filtered.filter(a => a.status_info?.status === 'missing' || a.is_missing);
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status_info?.status === statusFilter);
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => a.compliance_type === typeFilter);
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.building_name?.toLowerCase().includes(query) ||
        a.name?.toLowerCase().includes(query) ||
        a.compliance_type?.toLowerCase().includes(query)
      );
    }
    
    // Sort by priority (expired first, then expiring soon, then missing)
    filtered.sort((a, b) => {
      const priorityA = STATUS_PRIORITY[a.status_info?.status] || 5;
      const priorityB = STATUS_PRIORITY[b.status_info?.status] || 5;
      if (priorityA !== priorityB) return priorityA - priorityB;
      
      // Then by days until expiry
      const daysA = a.status_info?.days_until_expiry ?? 999;
      const daysB = b.status_info?.days_until_expiry ?? 999;
      return daysA - daysB;
    });
    
    setFilteredAlerts(filtered);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const labels = {
      expired: 'EXPIRED',
      expiring_soon: 'EXPIRING SOON',
      attention: 'NEEDS ATTENTION',
      missing: 'MISSING',
    };
    return (
      <Badge className={`${STATUS_COLORS[status] || 'bg-slate-100 text-slate-600'} border font-medium`}>
        {status === 'expired' && <XCircle className="w-3 h-3 mr-1" />}
        {status === 'expiring_soon' && <Clock className="w-3 h-3 mr-1" />}
        {status === 'attention' && <AlertTriangle className="w-3 h-3 mr-1" />}
        {status === 'missing' && <AlertCircle className="w-3 h-3 mr-1" />}
        {labels[status] || status?.toUpperCase()}
      </Badge>
    );
  };

  const getComplianceIcon = (type) => {
    const config = COMPLIANCE_CONFIG[type];
    if (!config) return <Shield className="w-5 h-5 text-slate-400" />;
    const Icon = config.icon;
    return <Icon className={`w-5 h-5 text-${config.color}-500`} />;
  };

  const handleExportCSV = () => {
    const csvData = filteredAlerts.map(a => ({
      'Building': a.building_name,
      'Address': a.building_address,
      'Compliance Type': a.name || a.compliance_type,
      'Status': a.status_info?.status,
      'Expiry Date': a.expiry_date ? formatDate(a.expiry_date) : 'N/A',
      'Days Remaining': a.status_info?.days_until_expiry ?? 'N/A',
      'Certificate #': a.certificate_number || 'N/A',
    }));
    
    const headers = Object.keys(csvData[0] || {});
    const csv = [
      headers.join(','),
      ...csvData.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance_alerts_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('CSV exported successfully');
  };

  const getBackPath = () => {
    if (user?.role === 'platform_owner') return '/platform';
    if (['managing_agent', 'agent_admin'].includes(user?.role)) return '/agent';
    return '/admin';
  };

  // Group alerts by building for building view
  const alertsByBuilding = filteredAlerts.reduce((acc, alert) => {
    const key = alert.hoa_id;
    if (!acc[key]) {
      acc[key] = {
        hoa_id: alert.hoa_id,
        building_name: alert.building_name,
        building_address: alert.building_address,
        alerts: [],
        expired: 0,
        expiring: 0,
        missing: 0,
      };
    }
    acc[key].alerts.push(alert);
    if (alert.status_info?.status === 'expired') acc[key].expired++;
    else if (['expiring_soon', 'attention'].includes(alert.status_info?.status)) acc[key].expiring++;
    else if (alert.status_info?.status === 'missing' || alert.is_missing) acc[key].missing++;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="compliance-alerts-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-amber-500" />
                  Compliance Alerts
                </h1>
                <p className="text-sm text-slate-500">
                  Monitor and manage compliance across all buildings
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={daysFilter} onValueChange={setDaysFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Next 30 days</SelectItem>
                  <SelectItem value="60">Next 60 days</SelectItem>
                  <SelectItem value="90">Next 90 days</SelectItem>
                  <SelectItem value="180">Next 6 months</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchAlerts}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleExportCSV} disabled={filteredAlerts.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className={`cursor-pointer transition-all ${activeTab === 'all' ? 'ring-2 ring-indigo-500' : 'hover:shadow-md'}`} onClick={() => setActiveTab('all')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900">{summary.total}</p>
                  <p className="text-sm text-slate-500">Total Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer transition-all border-red-200 ${activeTab === 'expired' ? 'ring-2 ring-red-500 bg-red-50' : 'hover:shadow-md'}`} onClick={() => setActiveTab('expired')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-red-600">{summary.expired}</p>
                  <p className="text-sm text-slate-500">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer transition-all border-amber-200 ${activeTab === 'expiring' ? 'ring-2 ring-amber-500 bg-amber-50' : 'hover:shadow-md'}`} onClick={() => setActiveTab('expiring')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-600">{summary.expiring_soon}</p>
                  <p className="text-sm text-slate-500">Expiring Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer transition-all ${activeTab === 'missing' ? 'ring-2 ring-slate-500 bg-slate-100' : 'hover:shadow-md'}`} onClick={() => setActiveTab('missing')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-700">{summary.missing}</p>
                  <p className="text-sm text-slate-500">Missing Records</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by building name or compliance type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                  <SelectItem value="attention">Needs Attention</SelectItem>
                  <SelectItem value="missing">Missing</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Compliance Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(COMPLIANCE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Badge variant="outline" className="text-slate-600">
                {filteredAlerts.length} alerts
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for View Mode */}
        <Tabs defaultValue="list" className="space-y-4">
          <TabsList>
            <TabsTrigger value="list">List View</TabsTrigger>
            <TabsTrigger value="building">By Building</TabsTrigger>
          </TabsList>

          {/* List View */}
          <TabsContent value="list">
            {filteredAlerts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">All Clear!</h3>
                  <p className="text-slate-500">No compliance alerts match your filters</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Building</TableHead>
                        <TableHead>Compliance Type</TableHead>
                        <TableHead>Expiry Date</TableHead>
                        <TableHead>Days Left</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAlerts.map((alert, idx) => (
                        <TableRow key={idx} className={alert.status_info?.status === 'expired' ? 'bg-red-50/50' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{alert.building_name}</p>
                                <p className="text-xs text-slate-500 truncate max-w-48">{alert.building_address}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getComplianceIcon(alert.compliance_type)}
                              <span>{alert.name || COMPLIANCE_CONFIG[alert.compliance_type]?.label || alert.compliance_type}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {alert.is_missing ? (
                              <span className="text-slate-400 italic">Not on file</span>
                            ) : (
                              <span className={alert.status_info?.status === 'expired' ? 'text-red-600 font-medium' : ''}>
                                {formatDate(alert.expiry_date)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {alert.status_info?.days_until_expiry !== null && alert.status_info?.days_until_expiry !== undefined ? (
                              <span className={`font-medium ${
                                alert.status_info.days_until_expiry < 0 ? 'text-red-600' :
                                alert.status_info.days_until_expiry <= 30 ? 'text-amber-600' :
                                'text-slate-600'
                              }`}>
                                {alert.status_info.days_until_expiry < 0 
                                  ? `${Math.abs(alert.status_info.days_until_expiry)} days overdue`
                                  : `${alert.status_info.days_until_expiry} days`}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(alert.status_info?.status)}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigate(
                                user?.role === 'platform_owner' 
                                  ? `/platform/building/${alert.hoa_id}/record`
                                  : `/agent/building/${alert.hoa_id}/record`
                              )}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Building View */}
          <TabsContent value="building">
            {Object.keys(alertsByBuilding).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">All Clear!</h3>
                  <p className="text-slate-500">No compliance alerts match your filters</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.values(alertsByBuilding)
                  .sort((a, b) => (b.expired + b.expiring) - (a.expired + a.expiring))
                  .map((building) => (
                  <Card 
                    key={building.hoa_id} 
                    className={`hover:shadow-md transition-shadow cursor-pointer ${
                      building.expired > 0 ? 'border-red-200' : 
                      building.expiring > 0 ? 'border-amber-200' : ''
                    }`}
                    onClick={() => navigate(
                      user?.role === 'platform_owner' 
                        ? `/platform/building/${building.hoa_id}/record`
                        : `/agent/building/${building.hoa_id}/record`
                    )}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{building.building_name}</CardTitle>
                            <CardDescription className="text-xs truncate max-w-40">
                              {building.building_address}
                            </CardDescription>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        {building.expired > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="text-sm font-medium text-red-600">{building.expired} expired</span>
                          </div>
                        )}
                        {building.expiring > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                            <span className="text-sm font-medium text-amber-600">{building.expiring} expiring</span>
                          </div>
                        )}
                        {building.missing > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-slate-400" />
                            <span className="text-sm font-medium text-slate-600">{building.missing} missing</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-slate-500">
                          {building.alerts.length} total alert{building.alerts.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Compliance Types Legend */}
        <Card className="mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Compliance Types Tracked</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(COMPLIANCE_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    <Icon className={`w-4 h-4 text-${config.color}-500`} />
                    <span className="text-slate-600">{config.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
